import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/uiSlice";
import { adminApi } from "../../services/api";

const STATUS_OPTIONS = [
  "new_lead", "pending", "called", "follow_up", "reached", "not_interested",
  "rejected", "forwarded", "account_made", "closed",
  "underage", "ftd", "call_back", "no_answer", "no_language",
];

const TeamLeadDetail = ({ teamLeadId, onBack }) => {
  const dispatch = useDispatch();

  const [teamLead, setTeamLead] = useState(null);
  const [members, setMembers] = useState([]);
  const [available, setAvailable] = useState([]);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Team management
  const [staffToAdd, setStaffToAdd] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState({});

  // Bulk assign
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  // Detail (assign down)
  const [selectedStaff, setSelectedStaff] = useState({});
  const [assigning, setAssigning] = useState({});

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [teamRes, leadsRes] = await Promise.all([
        adminApi.get(`/admin/team-leads/${teamLeadId}/team`),
        adminApi.get(`/admin/team-leads/${teamLeadId}/leads`),
      ]);
      setTeamLead(teamRes.data?.teamLead || null);
      setMembers(teamRes.data?.members || []);
      setAvailable(teamRes.data?.available || []);
      setLeads(leadsRes.data?.leads || []);
    } catch (e) {
      dispatch(
        showToast({ message: "Failed to load team lead detail", type: "error" }),
      );
    } finally {
      setIsLoading(false);
    }
  }, [teamLeadId, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddMember = async () => {
    if (!staffToAdd) return;
    setAdding(true);
    try {
      await adminApi.post(`/admin/team-leads/${teamLeadId}/members`, {
        staffId: staffToAdd,
      });
      dispatch(showToast({ message: "Team member added", type: "success" }));
      setStaffToAdd("");
      await load();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to add member",
          type: "error",
        }),
      );
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (staffId) => {
    if (!confirm("Remove this staff member from the team lead's team?")) return;
    setRemoving((prev) => ({ ...prev, [staffId]: true }));
    try {
      await adminApi.delete(
        `/admin/team-leads/${teamLeadId}/members/${staffId}`,
      );
      dispatch(showToast({ message: "Team member removed", type: "success" }));
      await load();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to remove member",
          type: "error",
        }),
      );
    } finally {
      setRemoving((prev) => ({ ...prev, [staffId]: false }));
    }
  };

  // ─── Assign-down (single) ───────────────────────────────────────────
  const handleAssignDown = async (leadId) => {
    const staffId = selectedStaff[leadId];
    if (!staffId) {
      dispatch(
        showToast({ message: "Select a team member first", type: "error" }),
      );
      return;
    }
    setAssigning((prev) => ({ ...prev, [leadId]: true }));
    try {
      const res = await adminApi.put(`/leads/${leadId}/assign`, {
        staffId,
      });
      // admin can use the normal assign endpoint (not restricted like TL self-service)
      if (res.data?.lead) {
        setLeads((prev) =>
          prev.map((l) => (l.id === leadId ? res.data.lead : l)),
        );
      }
      dispatch(showToast({ message: "Lead assigned to member", type: "success" }));
      setSelectedStaff((prev) => ({ ...prev, [leadId]: "" }));
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Failed to assign",
          type: "error",
        }),
      );
    } finally {
      setAssigning((prev) => ({ ...prev, [leadId]: false }));
    }
  };

  // ─── Bulk assign to TL ─────────────────────────────────────────────
  const toggleLead = (leadId) => {
    const next = new Set(selectedLeadIds);
    if (next.has(leadId)) next.delete(leadId);
    else next.add(leadId);
    setSelectedLeadIds(next);
    setSelectAll(
      leads.length > 0 && leads.every((l) => next.has(l.id)),
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedLeadIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedLeadIds(new Set(leads.map((l) => l.id)));
      setSelectAll(true);
    }
  };

  const handleBulkAssignToTeamLead = async () => {
    if (selectedLeadIds.size === 0) {
      dispatch(showToast({ message: "Select leads first", type: "error" }));
      return;
    }
    if (
      !confirm(
        `Assign ${selectedLeadIds.size} lead(s) to ${teamLead?.firstName} ${teamLead?.lastName}'s team lead ownership?`,
      )
    )
      return;
    setBulkAssigning(true);
    try {
      const res = await adminApi.post(
        `/admin/team-leads/${teamLeadId}/bulk-assign`,
        { leadIds: Array.from(selectedLeadIds) },
      );
      dispatch(
        showToast({
          message: `${res.data?.assigned || 0} lead(s) assigned to team lead`,
          type: "success",
        }),
      );
      setSelectedLeadIds(new Set());
      setSelectAll(false);
      setShowBulkAssign(false);
      await load();
    } catch (e) {
      dispatch(
        showToast({
          message: e.response?.data?.message || "Bulk assign failed",
          type: "error",
        }),
      );
    } finally {
      setBulkAssigning(false);
    }
  };

  // ─── Renderers ─────────────────────────────────────────────────────

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      new_lead: "bg-[#059669] text-white",
      pending: "bg-[#d97706] text-white",
      called: "bg-[#2563eb] text-white",
      follow_up: "bg-[#7c3aed] text-white",
      reached: "bg-[#0d9488] text-white",
      not_interested: "bg-[#64748b] text-white",
      rejected: "bg-[#dc2626] text-white",
      forwarded: "bg-[#1e3a5f] text-white",
      account_made: "bg-[#059669] text-white",
      underage: "bg-[#dc2626] text-white",
      ftd: "bg-[#0d9488] text-white",
      call_back: "bg-[#d97706] text-white",
      no_answer: "bg-[#d97706] text-white",
      no_language: "bg-[#ec4899] text-white",
      closed: "bg-[#64748b] text-white",
    };
    const displayName =
      status === "new_lead"
        ? "NEW LEAD"
        : status
          ? status.replace(/_/g, " ").toUpperCase()
          : "NEW LEAD";
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider border-b-[2px] ${colors[status] || "bg-[#94a3b8] text-white border-[#64748b]"}`}
      >
        {displayName}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-10 h-10 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!teamLead) {
    return (
      <div className="text-center py-20">
        <p className="text-[#475569] font-syne font-extrabold uppercase tracking-wider">
          Team lead not found
        </p>
        <button
          onClick={onBack}
          className="mt-4 px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider border-b-[2px] border-[#0b1e33]"
        >
          <i className="fas fa-arrow-left mr-1.5" /> Back
        </button>
      </div>
    );
  }

  const isRetention = teamLead.staffType === "retention";

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <button
          onClick={onBack}
          className="w-10 h-10 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#64748b] flex items-center justify-center hover:border-[#1e3a5f] hover:text-[#1e3a5f] transition-all"
          title="Back to team leads"
        >
          <i className="fas fa-arrow-left" />
        </button>
        <div
          className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md flex-shrink-0 ${
            isRetention
              ? "bg-gradient-to-br from-[#d97706] to-[#b45309] shadow-[#d97706]/25"
              : "bg-gradient-to-br from-[#059669] to-[#047857] shadow-[#059669]/25"
          }`}
        >
          <span className="text-lg font-extrabold text-white">
            {teamLead.firstName?.[0] || ""}
            {teamLead.lastName?.[0] || ""}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">
            {teamLead.firstName} {teamLead.lastName}
          </h2>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {isRetention ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-[#fef3c7] text-[#92400e] text-[10px] font-mono font-extrabold uppercase tracking-wider border-2 border-[#fcd34d]">
                <i className="fas fa-lock text-[9px]" /> RETENTION TL
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-[#ecfdf5] text-[#059669] text-[10px] font-mono font-extrabold uppercase tracking-wider border-2 border-[#a7f3d0]">
                <i className="fas fa-rotate text-[9px]" /> CONVERSION TL
              </span>
            )}
            <span className="text-[10px] text-[#64748b] font-mono font-bold">
              {teamLead.email}
            </span>
            <span className="text-[10px] text-[#64748b] font-mono font-bold">
              · {members.length} team member{members.length !== 1 ? "s" : ""}
            </span>
            <span className="text-[10px] text-[#64748b] font-mono font-bold">
              · {leads.length} lead{leads.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
        <button
          onClick={load}
          className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
        >
          <i className="fas fa-sync-alt mr-1.5" /> REFRESH
        </button>
      </div>

      {/* TEAM SECTION */}
      <div className="p-5 border-b-2 border-[#e2e8f0]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#0d9488]/10 border-2 border-[#0d9488]/20 flex items-center justify-center">
            <i className="fas fa-users text-[#0d9488] text-sm" />
          </div>
          <div>
            <h3 className="font-syne text-sm font-extrabold text-[#0b1e33] uppercase tracking-tight">
              TEAM MEMBERS
            </h3>
            <p className="text-[10px] text-[#475569] font-mono font-bold">
              {members.length} staff assigned to this team lead
            </p>
          </div>
        </div>

        {/* Add member row */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select
            value={staffToAdd}
            onChange={(e) => setStaffToAdd(e.target.value)}
            className="flex-1 min-w-[200px] bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[11px] font-extrabold outline-none focus:border-[#0d9488] cursor-pointer"
          >
            <option value="">— Select staff to add —</option>
            {available.map((s) => (
              <option key={s.id} value={s.id}>
                {s.firstName} {s.lastName} ({s.staffType})
              </option>
            ))}
          </select>
          <button
            onClick={handleAddMember}
            disabled={!staffToAdd || adding}
            className="px-5 py-2 rounded-xl bg-[#0d9488] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#0f766e] disabled:opacity-50 transition-all border-b-[2px] border-[#0f766e]"
          >
            {adding ? (
              <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <i className="fas fa-plus mr-1.5" /> ADD TO TEAM
              </>
            )}
          </button>
        </div>

        {available.length === 0 && (
          <p className="text-[10px] text-[#92400e] font-mono font-bold mb-4 flex items-center gap-1">
            <i className="fas fa-info-circle" />
            No unassigned {teamLead.staffType} staff available to add.
          </p>
        )}

        {/* Members grid */}
        {members.length === 0 ? (
          <div className="text-center py-8 bg-[#f8fafc] border-2 border-dashed border-[#cbd5e1] rounded-xl">
            <p className="text-[11px] text-[#64748b] font-mono font-bold">
              No team members yet. Add one above.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {members.map((m) => (
              <div
                key={m.id}
                className="bg-white border-2 border-[#e2e8f0] rounded-xl p-3 flex items-center gap-3"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0b1e33] to-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                  <span className="text-[11px] font-extrabold text-white">
                    {(m.firstName?.[0] || "")}
                    {(m.lastName?.[0] || "")}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-extrabold text-[#0f172a] truncate">
                    {m.firstName} {m.lastName}
                  </div>
                  <div className="text-[9px] text-[#64748b] font-mono font-bold truncate">
                    {m.email}
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveMember(m.id)}
                  disabled={removing[m.id]}
                  className="w-8 h-8 rounded-lg bg-[#fef2f2] border-2 border-[#fecaca] text-[#dc2626] flex items-center justify-center hover:bg-[#fee2e2] hover:border-[#fca5a5] transition-all disabled:opacity-40"
                  title="Remove from team"
                >
                  {removing[m.id] ? (
                    <span className="inline-block w-3 h-3 border-2 border-[#dc2626] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <i className="fas fa-user-minus text-[10px]" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* LEADS SECTION */}
      <div className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#1e3a5f]/10 border-2 border-[#1e3a5f]/20 flex items-center justify-center">
              <i className="fas fa-list text-[#1e3a5f] text-sm" />
            </div>
            <div>
              <h3 className="font-syne text-sm font-extrabold text-[#0b1e33] uppercase tracking-tight">
                LEAD OWNERSHIP
              </h3>
              <p className="text-[10px] text-[#475569] font-mono font-bold">
                {leads.length} lead{leads.length !== 1 ? "s" : ""} under this
                team lead
                {selectedLeadIds.size > 0
                  ? ` · ${selectedLeadIds.size} selected`
                  : ""}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowBulkAssign((v) => !v)}
            className="px-4 py-2 rounded-xl bg-[#7c3aed] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#6d28d9] transition-all border-b-[2px] border-[#6d28d9]"
          >
            <i className="fas fa-users-gear mr-1.5" /> BULK ASSIGN TO TL
          </button>
        </div>

        {/* Bulk-assign helper panel (shows the current leads as candidates for TL ownership; typically you'd assign unowned leads but this re-affirms ownership on any selected) */}
        {showBulkAssign && (
          <div className="mb-4 p-4 rounded-xl bg-[#f5f3ff] border-2 border-[#c4b5fd]">
            <p className="text-[11px] text-[#6d28d9] font-mono font-extrabold uppercase tracking-wider mb-2">
              <i className="fas fa-info-circle mr-1.5" /> BULK ASSIGN OWNERSHIP
            </p>
            <p className="text-[10px] text-[#475569] font-mono font-bold mb-3">
              Select leads from the list below, then click the button to set{" "}
              <b>{teamLead.firstName} {teamLead.lastName}</b> as owner
              (teamLeadId). Only leads whose{" "}
              <b>lead_type = "{teamLead.staffType}"</b> will be assigned;
              others are skipped.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkAssignToTeamLead}
                disabled={bulkAssigning || selectedLeadIds.size === 0}
                className="px-5 py-2.5 rounded-xl bg-[#7c3aed] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#6d28d9] disabled:opacity-50 disabled:cursor-not-allowed transition-all border-b-[2px] border-[#6d28d9] flex items-center gap-2"
              >
                {bulkAssigning ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ASSIGNING...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check" /> ASSIGN{" "}
                    {selectedLeadIds.size} LEAD
                    {selectedLeadIds.size !== 1 ? "S" : ""}
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setSelectedLeadIds(new Set());
                  setSelectAll(false);
                  setShowBulkAssign(false);
                }}
                className="px-4 py-2.5 rounded-xl bg-white border-2 border-[#e2e8f0] text-[#475569] text-[10px] font-syne font-extrabold uppercase tracking-wider hover:bg-[#f1f5f9] transition-all"
              >
                CLEAR
              </button>
            </div>
          </div>
        )}

        {/* Select-all bar */}
        {leads.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2 bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-t-xl">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="w-4 h-4 rounded border-2 border-[#cbd5e1] text-[#1e3a5f] focus:ring-[#1e3a5f] cursor-pointer"
              />
              <span className="text-[10px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                Select All
              </span>
            </label>
            {selectedLeadIds.size > 0 && (
              <span className="text-[10px] text-[#1e3a5f] font-mono font-extrabold">
                ({selectedLeadIds.size} selected)
              </span>
            )}
          </div>
        )}

        {leads.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-[#cbd5e1] rounded-xl">
            <p className="text-[11px] text-[#64748b] font-mono font-bold">
              No leads under this team lead yet. Use Bulk Assign above.
            </p>
          </div>
        ) : (
          <div className="divide-y-2 divide-[#f1f5f9] border-2 border-[#e2e8f0] border-t-0 rounded-b-xl overflow-hidden">
            {leads.map((lead) => {
              const isSelected = selectedLeadIds.has(lead.id);
              const isAssigned = !!lead.assignedTo;
              return (
                <div
                  key={lead.id}
                  className={`p-4 transition-all ${isSelected ? "bg-[#f0fdfa]" : "bg-white hover:bg-[#f8fafc]"}`}
                >
                  <div className="flex items-start gap-3 flex-wrap md:flex-nowrap">
                    <div className="pt-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleLead(lead.id)}
                        className="w-4 h-4 rounded border-2 border-[#cbd5e1] text-[#1e3a5f] focus:ring-[#1e3a5f] cursor-pointer"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-syne text-sm font-extrabold text-[#0f172a]">
                          {lead.name}
                        </span>
                        {lead.short_id && (
                          <span className="text-[10px] font-mono font-extrabold text-[#1e3a5f] bg-[#eff6ff] border-2 border-[#bfdbfe] px-2 py-0.5 rounded-lg">
                            #{lead.short_id}
                          </span>
                        )}
                        {getStatusBadge(lead.status)}
                        {isAssigned ? (
                          <span className="text-[9px] font-mono font-extrabold text-[#059669] bg-[#ecfdf5] border border-[#a7f3d0] px-2 py-0.5 rounded-lg">
                            <i className="fas fa-user-check mr-1" />
                            {lead.assignedFirstName} {lead.assignedLastName}
                          </span>
                        ) : (
                          <span className="text-[9px] font-mono font-extrabold text-[#92400e] bg-[#fef3c7] border border-[#fcd34d] px-2 py-0.5 rounded-lg">
                            <i className="fas fa-hourglass-half mr-1" /> NOT YET
                            ASSIGNED
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-[#64748b] font-mono font-bold flex items-center gap-1 flex-wrap">
                        {lead.email} · {lead.phone} · Created{" "}
                        {formatDate(lead.createdAt)}
                      </div>
                    </div>

                    {/* Assign-down controls */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <select
                        value={selectedStaff[lead.id] || ""}
                        onChange={(e) =>
                          setSelectedStaff((prev) => ({
                            ...prev,
                            [lead.id]: e.target.value,
                          }))
                        }
                        className="bg-white border-2 border-[#cbd5e1] rounded-xl px-3 py-2 text-[#0f172a] text-[10px] font-extrabold outline-none focus:border-[#1e3a5f] cursor-pointer"
                      >
                        <option value="">— assign to member —</option>
                        {members.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.firstName} {m.lastName}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleAssignDown(lead.id)}
                        disabled={assigning[lead.id] || !selectedStaff[lead.id]}
                        className="px-3 py-2 rounded-xl bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap border-b-[2px] border-[#047857]"
                      >
                        {assigning[lead.id] ? (
                          <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          "ASSIGN"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamLeadDetail;